import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Signin from "./Signin";
import Signup from "./Register";
import OTPVerification from "./OTP";
import { toast } from "react-toastify";
import { FiMenu, FiX, FiHome, FiBox, FiInfo, FiLogOut, FiUser, FiPhone, FiBell } from "react-icons/fi";
import { RiWallet3Line } from "react-icons/ri";
import {
  USER_UPDATED_EVENT,
  clearSessionData,
  formatCurrency,
  getStoredUserName,
  getStoredUserId,
  getStoredUserWallet,
  syncSessionUser,
} from "../utils/sessionUser";
import {
  WEBSITE_NOTIFICATIONS_UPDATED_EVENT,
  fetchWebsiteNotifications,
} from "../utils/websiteNotificationsApi";

const Navbar = ({ comingsoon = false }) => {
  const [showSigninModal, setShowSigninModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [mobileForOTP, setMobileForOTP] = useState("");
  const [otpIntent, setOtpIntent] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showSessionExpiredPopup, setShowSessionExpiredPopup] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(localStorage.getItem("token")));
  const [walletBalance, setWalletBalance] = useState(getStoredUserWallet());
  const [userName, setUserName] = useState(getStoredUserName());
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = useMemo(
    () => [
      { label: "Home", path: "/", icon: FiHome },
      { label: "Orders", path: "/orders", icon: FiBox },
      { label: "About us", path: "/about-us", icon: FiInfo },
      { label: "Contact Us", path: "/contact", icon: FiPhone },
    ],
    []
  );

  const isActivePath = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    clearSessionData();
    setIsLoggedIn(false);
    setWalletBalance(0);
    setUserName("User");
    setShowLogoutPopup(false);
    setShowSessionExpiredPopup(false);
    toast.success("Logged out successfully", { position: "bottom-center" });
    navigate("/");
  };

  const checkTokenAndNavigate = (destination, e) => {
    const token = localStorage.getItem("token");
    const requiresAuth = destination === "/orders" || destination === "/wallet";

    if (!requiresAuth) {
      navigate(destination);
      return;
    }

    if (!token) {
      e?.preventDefault?.();
      setShowSigninModal(true);
      return;
    }

    navigate(destination);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    const syncNavbarState = (event) => {
      const nextUser = event?.detail;
      setIsLoggedIn(Boolean(localStorage.getItem("token")));
      setWalletBalance(
        typeof nextUser?.wallet !== "undefined"
          ? Number(nextUser.wallet) || 0
          : getStoredUserWallet()
      );
      setUserName(nextUser?.fullname || getStoredUserName());
    };

    window.addEventListener(USER_UPDATED_EVENT, syncNavbarState);

    if (localStorage.getItem("token")) {
      syncSessionUser().catch(() => { });
    }

    return () => {
      window.removeEventListener(USER_UPDATED_EVENT, syncNavbarState);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const syncUnreadNotifications = async () => {
      const token = localStorage.getItem("token");
      const userId = getStoredUserId();

      if (!token || !userId) {
        if (isMounted) {
          setUnreadNotificationCount(0);
        }
        return;
      }

      try {
        const notifications = await fetchWebsiteNotifications({ token, userId });
        if (!isMounted) {
          return;
        }

        setUnreadNotificationCount(
          notifications.filter((notification) => !notification?.isRead).length
        );
      } catch (_) {
        if (isMounted) {
          setUnreadNotificationCount(0);
        }
      }
    };

    const handleNotificationsUpdated = (event) => {
      const explicitUnreadCount = event?.detail?.unreadCount;
      if (typeof explicitUnreadCount === "number") {
        setUnreadNotificationCount(explicitUnreadCount);
        return;
      }
      syncUnreadNotifications();
    };

    syncUnreadNotifications();
    const intervalId = window.setInterval(syncUnreadNotifications, 15000);
    window.addEventListener(USER_UPDATED_EVENT, syncUnreadNotifications);
    window.addEventListener(
      WEBSITE_NOTIFICATIONS_UPDATED_EVENT,
      handleNotificationsUpdated
    );

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener(USER_UPDATED_EVENT, syncUnreadNotifications);
      window.removeEventListener(
        WEBSITE_NOTIFICATIONS_UPDATED_EVENT,
        handleNotificationsUpdated
      );
    };
  }, [isLoggedIn, location.pathname]);

  const notificationButton = (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        checkTokenAndNavigate("/notifications", e);
        closeMenu();
      }}
      className="relative inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#eadfcb] bg-white text-slate-700 shadow-sm transition hover:border-[#f1b400] hover:text-[#1f2432]"
      aria-label="Open notifications"
    >
      <FiBell className="text-[20px]" />
      {unreadNotificationCount > 0 ? (
        <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[#ef4444] px-1.5 text-[10px] font-bold text-white shadow-sm">
          {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
        </span>
      ) : null}
    </button>
  );

  // const headerBackgroundStyle = {
  //   backgroundImage:
  //     "url('/assets/BG/nav_img.png')",
  //   backgroundSize: "cover",
  //   backgroundPosition: "center center",
  //   backgroundRepeat: "no-repeat",
  // };

//  const navratriFestiveNavbarBackgroundStyle = {
//   backgroundImage:
//     "url('/assets/festive/navbar_fes_navratri.png')",
//   backgroundSize: "100% 100%",
//   backgroundPosition: "center center",
//   backgroundRepeat: "no-repeat",
// };

  return (
    <>
      <style>{`
  @media (max-width: 639px) {
    .festival-navbar {
      width: 100% !important;

      // background-image:
      //   url('/assets/festive/navbar_fes_navratri_mob.png') !important;

      // background-size: 100% 100% !important;
      // background-position: center center !important;
      // background-repeat: no-repeat !important;

      padding-left: 14px !important;
      padding-right: 14px !important;
    }
  }
`}</style>

      {comingsoon ? (
        <nav
          // style={headerBackgroundStyle}
          className="px-4 sm:px-10 flex justify-center items-center shadow-lg shadow-[#0000000e] w-full sm:w-[95vw] rounded-b-[2rem] sm:rounded-b-[2.4rem] h-[72px] sm:h-[104px] z-50 mx-auto overflow-hidden bg-white"
        >
          <img src="/assets/logo/logo.png" alt="Instaport Logo" className="h-16 sm:h-20 w-auto" />
        </nav>
      ) : (
        <nav
  // style={navratriFestiveNavbarBackgroundStyle}
  className="
    festival-navbar
    px-4 sm:px-10
    flex
    justify-between
    items-center
    shadow-md
    w-full
    sm:w-[90vw]
    rounded-b-3xl
    h-[70px]
    sm:h-[90px]
    z-50
    mx-auto
    relative
    overflow-hidden
  "
>

          {/* Logo */}
          <div className="relative z-20 flex items-center gap-2 shrink-0">
            <Link to="/">
              <img src="/assets/logo/logo.png" alt="Instaport Logo" className="h-16 sm:h-20 w-auto" />
            </Link>
          </div>

          {/* Desktop Links */}
          <div className="hidden sm:flex items-center gap-3 rounded-full border border-[#f0e6d3] bg-white px-3 py-2 text-gray-700 shadow-[0_10px_28px_rgba(15,23,42,0.08)] font-medium">
            {navItems.map(({ label, path }) => (
              <Link
                key={path}
                to={path}
                onClick={(e) => {
                  e.preventDefault();
                  checkTokenAndNavigate(path, e);
                }}
                className={`relative rounded-full px-4 py-2.5 text-sm font-semibold transition ${isActivePath(path)
                  ? "bg-[linear-gradient(180deg,#fff4d0_0%,#ffe8a5_100%)] text-[#1b5e20] shadow-[0_8px_22px_rgba(241,180,0,0.18)]"
                  : "text-slate-700 hover:bg-[#fff8eb] hover:text-[#1f6d39]"
                  }`}
              >
                {label}
                {isActivePath(path) ? (
                  <span className="absolute inset-x-4 -bottom-1.5 h-[3px] rounded-full bg-[#f1b400]" />
                ) : null}
              </Link>
            ))}
            {!isLoggedIn ? (
              <button
                onClick={() => setShowSigninModal(true)}
                className="ml-6 bg-yellow-500 text-white px-5 py-2 rounded-lg shadow hover:bg-yellow-600 transition"
              >
                Sign In
              </button>
            ) : (
              <div className="flex items-center gap-6">
                {notificationButton}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    checkTokenAndNavigate("/wallet", e);
                  }}
                  className="inline-flex items-center gap-3 rounded-full border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-2 text-sm font-semibold text-slate-800 shadow-[0_12px_35px_rgba(245,158,11,0.16)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(245,158,11,0.2)]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-amber-600 shadow-sm">
                    <RiWallet3Line className="text-lg" />
                  </span>
                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Wallet
                    </span>
                    <span className="text-sm font-bold text-slate-900">
                      {formatCurrency(walletBalance)}
                    </span>
                  </span>
                </button>
                <span className="text-gray-700">Welcome, {userName}</span>
                <button
                  onClick={() => setShowLogoutPopup(true)}
                  className="flex items-center gap-1 text-red-500 hover:text-red-700 transition"
                >
                  <FiLogOut /> Logout
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="sm:hidden relative z-20 shrink-0 text-3xl text-gray-700 focus:outline-none"
            onClick={toggleMenu}
          >
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </button>

          {/* Mobile Sidebar */}
          <div
            className={`fixed top-0 left-0 h-full w-75 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${isMenuOpen ? "translate-x-0" : "-translate-x-full"
              }`}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <img src="/assets/logo/logo.png" alt="Instaport Logo" className="h-16 sm:h-20 w-auto" />
              <button onClick={closeMenu} className="text-2xl text-gray-700">
                <FiX />
              </button>
            </div>

            <div className="flex flex-col mt-6 gap-3 px-6 text-lg font-medium text-gray-700">
              {navItems.map(({ label, path, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={(e) => {
                    e.preventDefault();
                    checkTokenAndNavigate(path, e);
                    closeMenu();
                  }}
                  className={`flex items-center gap-3 rounded-2xl px-4 py-3 transition ${isActivePath(path)
                    ? "bg-[linear-gradient(180deg,#fff4d0_0%,#ffe8a5_100%)] text-[#1b5e20] shadow-[0_8px_22px_rgba(241,180,0,0.16)]"
                    : "hover:bg-[#fff8eb] hover:text-[#1f6d39]"
                    }`}
                >
                  <Icon /> {label}
                </Link>
              ))}

              <div className="border-t pt-6 flex flex-col gap-4">
                {!isLoggedIn ? (
                  <button
                    onClick={() => {
                      setShowSigninModal(true);
                      closeMenu();
                    }}
                    className="bg-yellow-500 text-white px-4 py-2 rounded-lg shadow hover:bg-yellow-600 transition"
                  >
                    <FiUser className="inline-block mr-2" /> Sign In
                  </button>
                ) : (
                  <>
                    <div className="flex items-center justify-start">
                      {notificationButton}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        checkTokenAndNavigate("/wallet", e);
                        closeMenu();
                      }}
                      className="flex items-center justify-between rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 text-left shadow-sm"
                    >
                      <span className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-amber-600 shadow-sm">
                          <RiWallet3Line className="text-xl" />
                        </span>
                        <span className="flex flex-col">
                          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                            Wallet
                          </span>
                          <span className="text-base font-bold text-slate-900">
                            {formatCurrency(walletBalance)}
                          </span>
                        </span>
                      </span>
                    </button>
                    <span className="text-gray-700">Welcome, {userName}</span>
                    <button
                      onClick={() => {
                        setShowLogoutPopup(true);
                        closeMenu();
                      }}
                      className="flex items-center gap-2 text-red-500 hover:text-red-700 transition"
                    >
                      <FiLogOut /> Logout
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Overlay when sidebar is open */}
          {isMenuOpen && (
            <div
              onClick={closeMenu}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 sm:hidden"
            />
          )}
        </nav>


      )}

      {/* Logout Popup */}
      {showLogoutPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 animate-fadeIn">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Logout</h2>
            <p className="text-gray-500">Are you sure you want to logout?</p>
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowLogoutPopup(false)}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white shadow-md transition"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Expired Popup */}
      {showSessionExpiredPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 animate-fadeIn">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Session Expired</h2>
            <p className="text-gray-500">Your session has expired. Please login again.</p>
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => {
                  handleLogout();
                  setShowSigninModal(true);
                }}
                className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white shadow-md transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <Signin
        setOtpIntent={setOtpIntent}
        showSigninModal={showSigninModal}
        setShowSigninModal={setShowSigninModal}
        setShowSignupModal={setShowSignupModal}
        setShowOTPModal={setShowOTPModal}
        setMobileForOTP={setMobileForOTP}
      />
      <Signup
        setOtpIntent={setOtpIntent}
        showSignupModal={showSignupModal}
        setShowSignupModal={setShowSignupModal}
        setShowSigninModal={setShowSigninModal}
        setShowOTPModal={setShowOTPModal}
        setMobileForOTP={setMobileForOTP}
      />
      <OTPVerification
        otpIntent={otpIntent}
        setShowSignupModal={setShowSignupModal}
        showOTPModal={showOTPModal}
        setShowOTPModal={setShowOTPModal}
        setShowSigninModal={setShowSigninModal}
        mobile={mobileForOTP}
      />
    </>
  );
};

export default Navbar;
